const { PUBLIC_MESSAGES } = require("../constants")
const { fetchEventTarget } = require("../SSEPipeline")

const _getCapture = (options) => {
  if (typeof options === "boolean") return options
  return Boolean(options?.capture)
}

class FetchEventStream {
  _abortController = null
  _eventTarget = null
  _onmessage = null
  _onopen = null
  _onerror = null
  _config = null
  // Keyed by event type, holding every listener registered under it. A plain type-to-listener
  // record would drop all but the last, and the dropped ones stay attached after dispose.
  _listeners = new Map()

  get onmessage() {
    return this._onmessage
  }

  // Detaching needs the callback, not just the type, and addEventListener returns nothing -- so
  // the handler has to be held here or the previous one can never be removed.
  set onmessage(fn) {
    if (this._onmessage) {
      this.removeEventListener(PUBLIC_MESSAGES.MESSAGE, this._onmessage)
    }
    this._onmessage = fn
    this.addEventListener(PUBLIC_MESSAGES.MESSAGE, fn)
  }

  get onopen() {
    return this._onopen
  }

  set onopen(fn) {
    if (this._onopen) {
      this.removeEventListener(PUBLIC_MESSAGES.OPEN, this._onopen)
    }
    this._onopen = fn
    this.addEventListener(PUBLIC_MESSAGES.OPEN, fn)
  }

  get onerror() {
    return this._onerror
  }

  set onerror(fn) {
    if (this._onerror) {
      this.removeEventListener(PUBLIC_MESSAGES.ERROR, this._onerror)
    }
    this._onerror = fn
    this.addEventListener(PUBLIC_MESSAGES.ERROR, fn)
  }

  constructor(services) {
    this._config = services?.config
    this._logger = services?.logger
    this._auth = services?.auth
    this._eventTarget = fetchEventTarget({
      config: this._config,
      logger: this._logger,
      auth: this._auth,
    })
  }

  initialize(url, { inputSignal } = {}) {
    this._abortController = new AbortController()

    // if the incoming signal aborts, dispose resources and resolve:
    inputSignal?.addEventListener("abort", () => {
      this._dispose()
    })

    this._eventTarget.initializeFetchPipe({
      url,
      config: {
        method: "GET",
        signal: this._abortController.signal,
      },
    })

    return this
  }

  abort() {
    try {
      this._abortController?.abort()
    } catch (err) {
      this._logger?.error("[FetchEventStream] An error has occurred while attempting to abort the connection", err)
    }
  }

  close() {
    this._dispose()
  }

  addEventListener(type, listener, ...rest) {
    if (!this._listeners.has(type)) {
      this._listeners.set(type, [])
    }
    const options = rest[0]
    const list = this._listeners.get(type)
    const capture = _getCapture(options)
    const exists = list.some((item) => item.listener === listener && _getCapture(item.options) === capture)
    if (!exists) {
      list.push({ listener, options })
    }
    return this._eventTarget.addEventListener(type, listener, ...rest)
  }

  removeEventListener(type, listener, ...rest) {
    const list = this._listeners.get(type)
    if (list) {
      const options = rest[0]
      const capture = _getCapture(options)
      const index = list.findIndex((item) => item.listener === listener && _getCapture(item.options) === capture)
      if (index !== -1) {
        list.splice(index, 1)
      }
    }
    return this._eventTarget.removeEventListener(type, listener, ...rest)
  }

  dispatchEvent(...args) {
    return this._eventTarget.dispatchEvent(...args)
  }

  _dispose() {
    this.abort()
    // Detaching straight from the target rather than through removeEventListener, so the record
    // is not mutated while it is being walked.
    for (const [type, list] of this._listeners.entries()) {
      for (const { listener, options } of list) {
        if (options !== undefined) {
          this._eventTarget?.removeEventListener(type, listener, options)
        } else {
          this._eventTarget?.removeEventListener(type, listener)
        }
      }
    }
    this._listeners.clear()
    this._eventTarget = null
  }
}

FetchEventStream.PUBLIC_MESSAGES = PUBLIC_MESSAGES

module.exports = FetchEventStream
